import React, { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Typography,
  IconButton,
  Chip,
  Grid,
  Collapse,
  Box,
} from '@material-ui/core';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Comment,
} from '@material-ui/icons';
import moment from 'moment';
import NotesPopover from '../dialogs/notesPopover';
import RecreateNotesButton from '../payroll/recreateNotesButton';

const PayrollNotesTab = ({
  filteredPayrolls,
  sort,
  handelSort,
  payrollRun,
  currentUser = localStorage.getItem('user')
}) => {
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedPreviousRows, setExpandedPreviousRows] = useState({});

  const handleExpandClick = (employeeId) => {
    setExpandedRows(prev => ({
      ...prev,
      [employeeId]: !prev[employeeId]
    }));
  };

  const handleExpandPreviousClick = (employeeId) => {
    setExpandedPreviousRows(prev => ({
      ...prev,
      [employeeId]: !prev[employeeId]
    }));
  };

  const formatNote = (note) => {
    if (!note) return null;
    
    return (
      <Card key={note.id || Math.random()} variant="outlined" style={{ marginBottom: 8 }}>
        <CardContent style={{ padding: 12 }}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                {note.note}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                <Grid container spacing={1} alignItems="center">
                  <Grid item>
                    <Chip 
                      size="small" 
                      label={note.noteType || 'comment'} 
                      color="primary" 
                      variant="outlined" 
                    />
                  </Grid>
                  {note.noteField && (
                    <Grid item>
                      <Chip 
                        size="small" 
                        label={`Field: ${note.noteField}`} 
                        color="secondary" 
                        variant="outlined" 
                      />
                    </Grid>
                  )}
                </Grid>
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary">
                By: {note.noteBy || 'System'} | 
                Date: {moment(note.noteDate).format('DD/MM/YYYY HH:mm')} |
              
                {note.payrollDate && ` | Payroll Date: ${moment(note.payrollDate).format('MMM YYYY')}`}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const SortableTableCell = ({ field, children, width }) => (
    <TableCell 
      sortDirection={sort.field === field ? sort.order : false}
      style={{ width }}
    >
      <div 
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        onClick={() => handelSort(field)}
      >
        {children}
        {sort.field === field && (
          <Box component="span" ml={1}>
            {sort.order === 'ASC' ? '↑' : '↓'}
          </Box>
        )}
      </div>
    </TableCell>
  );

  const employeesWithNotes = filteredPayrolls ? filteredPayrolls.filter(payroll => payroll.notes && payroll.notes.length > 0) : [];
  
  if (!filteredPayrolls || filteredPayrolls.length === 0) {
    return (
      <div>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <RecreateNotesButton record={payrollRun} />
        </div>
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>OrgID</TableCell>
                <TableCell>Full Name</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No payroll data found
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      </div>
    );
  }
  
  if (employeesWithNotes.length === 0) {
    return (
      <div>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <RecreateNotesButton record={payrollRun} />
        </div>
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>OrgID</TableCell>
                <TableCell>Full Name</TableCell>
                <TableCell>Notes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No employees with notes found
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <RecreateNotesButton record={payrollRun} />
      </div>
      <Paper>
        <Table>
        <TableHead>
          <TableRow>
            <SortableTableCell field="orgID" width="15%">
              <Typography variant="subtitle2">OrgID</Typography>
            </SortableTableCell>
            <SortableTableCell field="fullName" width="25%">
              <Typography variant="subtitle2">Full Name</Typography>
            </SortableTableCell>
            <SortableTableCell field="notes" width="35%">
              <Typography variant="subtitle2">Notes</Typography>
            </SortableTableCell>
            <SortableTableCell field="employeeNotes" width="25%">
              <Typography variant="subtitle2">Previous Notes History</Typography>
            </SortableTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredPayrolls.filter(payroll => payroll.notes && payroll.notes.length > 0).map((payroll) => {
            const hasNotes = true; // Since we filtered for employees with notes
            const notesCount = payroll.notes.length;
            const orgID = payroll.orgID || payroll.employee.orgID || '';
            
            return (
              <React.Fragment key={payroll.employeeId}>
                <TableRow hover>
                  <TableCell>
                    <Typography variant="body2">
                      {orgID}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {payroll.fullName || payroll._fullName || payroll.employee.fullName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>
                        {hasNotes ? (
                          <IconButton
                            size="small"
                            onClick={() => handleExpandClick(payroll.employeeId)}
                            style={{ color: '#1976d2' }}
                          >
                            {expandedRows[payroll.employeeId] ? 
                              <KeyboardArrowUp /> : 
                              <KeyboardArrowDown />
                            }
                          </IconButton>
                        ) : (
                          <Comment color="disabled" fontSize="small" />
                        )}
                      </Grid>
                      <Grid item>
                        <Typography variant="body2" color="textSecondary">
                          {hasNotes ? (
                            <span>
                              <strong>{notesCount}</strong> note{notesCount !== 1 ? 's' : ''}
                              {payroll.notes[0] && (
                                <span style={{ marginLeft: 8, fontStyle: 'italic' }}>
                                  "{payroll.notes[0].note.substring(0, 50)}
                                  {payroll.notes[0].note.length > 50 ? '...' : ''}"
                                </span>
                              )}
                            </span>
                          ) : (
                            'No notes'
                          )}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <NotesPopover
                          notes={payroll.notes || []}
                          employeeId={payroll.employeeId}
                          payrollDate={payrollRun.payrollDate}
                          currentUser={currentUser}
                          onChange={(updatedNotes) => {
                            // Update the notes in the payroll object
                            payroll.notes = updatedNotes;
                            // Trigger a re-render by updating the parent component if needed
                          }}
                        />
                      </Grid>
                    </Grid>
                    <Collapse 
                      in={expandedRows[payroll.employeeId]} 
                      timeout="auto" 
                      unmountOnExit
                    >
                      <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          All Notes:
                        </Typography>
                        {hasNotes ? (
                          payroll.notes.map(formatNote)
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No notes available for this employee.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                  <TableCell>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>
                        {payroll.employee.notes && payroll.employee.notes.length > 0 ? (
                          <IconButton
                            size="small"
                            onClick={() => handleExpandPreviousClick(payroll.employeeId)}
                            style={{ color: '#1976d2' }}
                          >
                            {expandedPreviousRows[payroll.employeeId] ?
                              <KeyboardArrowUp /> :
                              <KeyboardArrowDown />
                            }
                          </IconButton>
                        ) : (
                          <Comment color="disabled" fontSize="small" />
                        )}
                      </Grid>
                      <Grid item>
                        <Typography variant="body2" color="textSecondary">
                          {payroll.employee.notes && payroll.employee.notes.length > 0 ? (
                            <span>
                              <strong>{payroll.employee.notes.length}</strong> note{payroll.employee.notes.length !== 1 ? 's' : ''}
                              {payroll.employee.notes[0] && (
                                <span style={{ marginLeft: 8, fontStyle: 'italic' }}>
                                  "{payroll.employee.notes[0].note.substring(0, 50)}{payroll.employee.notes[0].note.length > 50 ? '...' : ''}"
                                </span>
                              )}
                            </span>
                          ) : (
                            'No previous notes'
                          )}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Collapse
                      in={expandedPreviousRows[payroll.employeeId]}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box mt={2}>
                        <Typography variant="subtitle2" gutterBottom>
                          Previous Notes History:
                        </Typography>
                        {payroll.employee.notes && payroll.employee.notes.length > 0 ? (
                          payroll.employee.notes.map(formatNote)
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No previous notes available for this employee.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
      </Paper>
    </div>
  );
};

export default PayrollNotesTab;